import {
  NAV_SECTIONS,
  buildNavContext,
  resolveAuditLogPath,
  resolveMyTimesheetPath,
} from './navConfig';
import {
  shouldShowAuditLogNavNewBadge,
  shouldShowSkillNavNewBadge,
} from '../utils/featureDiscovery';

/**
 * @typedef {Object} ResolvedNavItem
 * @property {string} id
 * @property {string} label
 * @property {'link' | 'action'} type
 * @property {string} [path]
 * @property {string} [actionId]
 * @property {string} icon
 * @property {string} iconColor
 * @property {string[]} [matchPaths]
 * @property {boolean} [showNewBadge]
 * @property {boolean} [highlightDiscovery]
 */

/**
 * @typedef {Object} ResolvedNavSection
 * @property {string} id
 * @property {string} title
 * @property {ResolvedNavItem[]} items
 */

/** Case-insensitive A–Z sort for resolved nav item labels. */
const compareNavItemLabels = (a, b) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

/**
 * Build filtered navigation sections for the current user.
 * @param {object|null|undefined} user
 * @returns {ResolvedNavSection[]}
 */
/** Flat list of visible nav labels for the user (mobile drawer and desktop sidebar share this). */
export function getNavLinkLabels(user) {
  return buildNavSections(user)
    .flatMap((section) => section.items.map((item) => item.label))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/**
 * @param {object|null|undefined} user
 * @param {{ walkthroughEnabled?: boolean }} [options]
 */
export function buildNavSections(user, options = {}) {
  if (!user) return [];

  const ctx = {
    ...buildNavContext(user),
    walkthroughEnabled: Boolean(options.walkthroughEnabled),
  };
  const myTimesheetPath = resolveMyTimesheetPath(user, ctx);
  const auditLogPath = resolveAuditLogPath(user);

  return NAV_SECTIONS.map((section) => {
    const items = section.items
      .filter((item) => item.visible(user, ctx))
      .map((item) => {
        const label = item.resolveLabel ? item.resolveLabel(user, ctx) : item.label;
        let path = item.path;
        if (item.id === 'my-timesheet') {
          path = myTimesheetPath;
        } else if (item.id === 'my-audit-log') {
          path = auditLogPath;
        }
        let showNewBadge = false;
        if (item.discovery?.scope === 'auditLog') {
          showNewBadge = item.discovery?.badge === 'new' && shouldShowAuditLogNavNewBadge();
        } else if (item.discovery?.badge === 'new') {
          showNewBadge = shouldShowSkillNavNewBadge();
        }
        return {
          id: item.id,
          label,
          type: item.type,
          path,
          actionId: item.actionId,
          icon: item.icon,
          iconColor: item.iconColor,
          matchPaths: item.matchPaths,
          showNewBadge,
          highlightDiscovery: showNewBadge,
        };
      })
      .sort(compareNavItemLabels);

    const title = section.resolveSectionTitle
      ? section.resolveSectionTitle(user, ctx)
      : section.title;
    return items.length > 0 ? { id: section.id, title, items } : null;
  }).filter(Boolean);
}

/**
 * @param {string} pathname
 * @param {ResolvedNavItem} item
 */
export function isNavItemActive(pathname, item) {
  if (item.type !== 'link' || !item.path) return false;
  const paths = item.matchPaths?.length ? item.matchPaths : [item.path];
  return paths.some(
    (p) => pathname === p || (p !== '/employee' && pathname.startsWith(`${p}/`))
  );
}

export default buildNavSections;
